import React, { useEffect, useRef, useState } from 'react';
import {
    Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter, DrawerCloseButton, Box, Text, Input, InputGroup,
    InputLeftAddon, Textarea, Select, Button, Wrap, WrapItem, Switch, FormControl, FormLabel, FormErrorMessage, FormHelperText, Image,
    useToast, AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter
} from '@chakra-ui/react';
import { CloudUpload } from '@mui/icons-material';
import useIsPhone from '../../hooks/useIsPhone';

import { addProduct, updateProduct, deleteProduct } from '../../services/ProductServices';
import { uploadImageToCloudinary } from '../../services/ImageServices';
import { COLOR_SWATCHES } from '../ProductFilters';
import usePublicSettings from '../../hooks/usePublicSettings';

const SIZE_GROUPS = [
    { label: 'Tops', sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'] },
    { label: 'Waist', sizes: ['28', '30', '32', '34', '36', '38', '40'] }
];

const empty = { imageUrl: '', name: '', price: '', category: '', color: '', sizes: ['S', 'M', 'L', 'XL', 'XXL'], description: '', status: true };

const validate = (p) => {
    const e = {};
    if (!p.imageUrl.trim()) e.imageUrl = 'Add a product photo.';
    if (!p.name.trim()) e.name = 'Enter a product name.';
    if (!(Number(p.price) > 0)) e.price = 'Enter a price in rupees.';
    if (!p.category) e.category = 'Choose a category.';
    if (!p.color) e.color = 'Choose a colour.';
    if (!p.sizes.length) e.sizes = 'Select at least one size.';
    if (!p.description.trim()) e.description = 'Add a short description.';
    return e;
};

// Slide-up editor on mobile, side panel on desktop. `product` null = create new.
const ProductEditor = ({ isOpen, onClose, product, genres, categories, onSaved, onDeleted }) => {

    const toast = useToast();
    const placement = useIsPhone() ? 'bottom' : 'right';
    const CAN_UPLOAD = Boolean(usePublicSettings()?.cloudinary);
    const [form, setForm] = useState(empty);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const cancelRef = useRef();
    const fileRef = useRef();

    useEffect(() => {
        if (!isOpen) return;
        setErrors({});
        setForm(product ? {
            imageUrl: product.imageUrl || '', name: product.name || '', price: String(product.price ?? ''), category: product.category || '',
            color: product.color || '', sizes: product.sizes || [], description: product.description || '', status: product.status !== false
        } : empty);
    }, [isOpen, product]);

    const set = (key, value) => { setForm(f => ({ ...f, [key]: value })); setErrors(e => ({ ...e, [key]: null })); };
    const toggleSize = (size) => set('sizes', form.sizes.includes(size) ? form.sizes.filter(s => s !== size) : [...form.sizes, size]);

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const result = await uploadImageToCloudinary(file);
            if (!result.secure_url) throw new Error(result.error?.message || 'Upload failed');
            set('imageUrl', result.secure_url);
        } catch (error) {
            toast({ title: 'Upload failed', description: error.message, status: 'error', duration: 3000 });
        }
        setUploading(false);
        e.target.value = '';
    };

    const onSave = async () => {
        const found = validate(form);
        setErrors(found);
        if (Object.keys(found).length) return;
        setSaving(true);
        const payload = { ...form, name: form.name.trim(), description: form.description.trim(), price: Math.round(Number(form.price)), gender: 'man' };
        try {
            const result = product
                ? await updateProduct(product._id, payload)
                : await addProduct(payload);
            const saved = result.product || result.newProduct;
            if (!saved) throw new Error(result.error?.message || 'Could not save');
            toast({ title: product ? 'Product updated' : 'Product added', status: 'success', duration: 1800, position: 'top' });
            onSaved(saved);
            onClose();
        } catch (error) {
            toast({ title: 'Could not save', description: error.response?.data?.error?.message || error.message, status: 'error', duration: 3000 });
        }
        setSaving(false);
    };

    const onDelete = async () => {
        setConfirmDelete(false);
        try {
            await deleteProduct(product._id);
            toast({ title: 'Product deleted', status: 'success', duration: 1800, position: 'top' });
            onDeleted(product._id);
            onClose();
        } catch (error) {
            toast({ title: 'Could not delete', status: 'error', duration: 3000 });
        }
    };

    return (
        <>
            <Drawer isOpen={isOpen} placement={placement || 'bottom'} onClose={onClose} size='md' >
                <DrawerOverlay />
                <DrawerContent borderTopRadius={{ base: 16, md: 0 }} maxHeight={{ base: '92vh', md: '100vh' }} >
                    <DrawerCloseButton />
                    <DrawerHeader borderBottomWidth='1px' fontSize={18} >{product ? 'Edit product' : 'New product'}</DrawerHeader>
                    <DrawerBody py={4} >
                        <FormControl isInvalid={!!errors.imageUrl} mb={4} >
                            <FormLabel fontSize={14} mb={1} >Photo</FormLabel>
                            <Box display='flex' gap={3} alignItems='center' >
                                <Box width='88px' height='116px' flexShrink={0} borderRadius='6px' bg='raano.imageBg' overflow='hidden' border='1px solid' borderColor='raano.border' >
                                    {form.imageUrl && <Image src={form.imageUrl} alt='' width='100%' height='100%' objectFit='cover' />}
                                </Box>
                                <Box flex={1} >
                                    {CAN_UPLOAD &&
                                        <Button variant='wipe' height='36px' fontSize='13px' width='100%' mb={2} leftIcon={<CloudUpload fontSize='small' />} isLoading={uploading} onClick={() => fileRef.current?.click()} >Upload photo</Button>}
                                    <input ref={fileRef} type='file' accept='image/*' hidden onChange={onUpload} />
                                    <Input size='sm' borderRadius='6px' placeholder='…or paste an image link' value={form.imageUrl} onChange={(e) => set('imageUrl', e.target.value)} />
                                </Box>
                            </Box>
                            {errors.imageUrl ? <FormErrorMessage>{errors.imageUrl}</FormErrorMessage>
                                : !CAN_UPLOAD && <FormHelperText fontSize={12} >Photo upload turns on once Cloudinary is connected in Admin → Integrations.</FormHelperText>}
                        </FormControl>

                        <FormControl isInvalid={!!errors.name} mb={4} >
                            <FormLabel fontSize={14} mb={1} >Name</FormLabel>
                            <Input placeholder='e.g. Navy Linen Shirt' value={form.name} onChange={(e) => set('name', e.target.value)} />
                            <FormErrorMessage>{errors.name}</FormErrorMessage>
                        </FormControl>

                        <Box display='flex' gap={3} mb={4} >
                            <FormControl isInvalid={!!errors.price} flex={1} >
                                <FormLabel fontSize={14} mb={1} >Price</FormLabel>
                                <InputGroup>
                                    <InputLeftAddon children='₹' />
                                    <Input type='number' inputMode='numeric' min={1} placeholder='1499' value={form.price} onChange={(e) => set('price', e.target.value)} />
                                </InputGroup>
                                <FormErrorMessage>{errors.price}</FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={!!errors.category} flex={1} >
                                <FormLabel fontSize={14} mb={1} >Category</FormLabel>
                                <Select placeholder='Choose' value={form.category} onChange={(e) => set('category', e.target.value)} >
                                    {genres.map(g => (
                                        <optgroup key={g._id} label={g.name} >
                                            {categories.filter(c => c.genre === g._id).map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                        </optgroup>
                                    ))}
                                </Select>
                                <FormErrorMessage>{errors.category}</FormErrorMessage>
                            </FormControl>
                        </Box>

                        <FormControl isInvalid={!!errors.color} mb={4} >
                            <FormLabel fontSize={14} mb={2} >Colour {form.color && <Text as='span' fontWeight={400} color='blackAlpha.600' textTransform='capitalize' >· {form.color}</Text>}</FormLabel>
                            <Wrap spacing={2} p='3px' >
                                {Object.entries(COLOR_SWATCHES).map(([name, hex]) => (
                                    <WrapItem key={name} >
                                        <Box as='button' type='button' aria-label={name} aria-pressed={form.color === name} title={name} boxSize='32px' borderRadius='full' bg={hex}
                                            border='1px solid' borderColor='blackAlpha.300'
                                            boxShadow={form.color === name ? '0 0 0 2px #fff, 0 0 0 4px #000' : 'none'}
                                            onClick={() => set('color', name)} />
                                    </WrapItem>
                                ))}
                            </Wrap>
                            <FormErrorMessage>{errors.color}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!errors.sizes} mb={4} >
                            <FormLabel fontSize={14} mb={1} >Sizes in stock</FormLabel>
                            {SIZE_GROUPS.map(group => (
                                <Box key={group.label} mb={2} >
                                    <Text fontSize={12} color='blackAlpha.600' mb={1} >{group.label}</Text>
                                    <Wrap spacing={2} >
                                        {group.sizes.map(size => (
                                            <WrapItem key={size} >
                                                <Button size='sm' minWidth='44px' borderRadius='sizeBtn' fontWeight={600} border='1px solid' borderColor={form.sizes.includes(size) ? 'raano.black' : 'raano.sizeBorder'} bg={form.sizes.includes(size) ? 'raano.black' : 'raano.white'} color={form.sizes.includes(size) ? 'raano.white' : 'raano.black'} _hover={{ borderColor: 'raano.black' }} onClick={() => toggleSize(size)} >{size}</Button>
                                            </WrapItem>
                                        ))}
                                    </Wrap>
                                </Box>
                            ))}
                            <FormErrorMessage>{errors.sizes}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!errors.description} mb={4} >
                            <FormLabel fontSize={14} mb={1} >Description</FormLabel>
                            <Textarea rows={3} placeholder='Fabric, fit and what it pairs with' value={form.description} onChange={(e) => set('description', e.target.value)} />
                            <FormErrorMessage>{errors.description}</FormErrorMessage>
                        </FormControl>

                        <FormControl display='flex' alignItems='center' justifyContent='space-between' p={3} bg='raano.surface' borderRadius='8px' >
                            <Box>
                                <FormLabel htmlFor='product-status' mb={0} fontSize={14} >Show in store</FormLabel>
                                <Text fontSize={12} color='blackAlpha.600' >Hidden products stay in orders but can't be bought.</Text>
                            </Box>
                            <Switch id='product-status' isChecked={form.status} onChange={(e) => set('status', e.target.checked)} />
                        </FormControl>

                        {product &&
                            <Button mt={5} width='100%' variant='ghost' colorScheme='red' size='sm' onClick={() => setConfirmDelete(true)} >Delete product</Button>}
                    </DrawerBody>
                    <DrawerFooter borderTopWidth='1px' gap={3} >
                        <Button flex={1} variant='wipe' height='44px' fontSize='15px' onClick={onClose} >Cancel</Button>
                        <Button flex={2} variant='ink' height='44px' fontSize='15px' isLoading={saving} onClick={onSave} >{product ? 'Save changes' : 'Add product'}</Button>
                    </DrawerFooter>
                </DrawerContent>
            </Drawer>

            <AlertDialog isOpen={confirmDelete} leastDestructiveRef={cancelRef} onClose={() => setConfirmDelete(false)} isCentered >
                <AlertDialogOverlay>
                    <AlertDialogContent mx={4} borderRadius='12px' >
                        <AlertDialogHeader fontSize={18} >Delete {product?.name}?</AlertDialogHeader>
                        <AlertDialogBody fontSize={14} color='blackAlpha.700' >This can't be undone. To stop selling it for now, turn off “Show in store” instead.</AlertDialogBody>
                        <AlertDialogFooter gap={2} >
                            <Button ref={cancelRef} variant='wipe' height='40px' px='18px' fontSize='14px' onClick={() => setConfirmDelete(false)} >Keep</Button>
                            <Button height='40px' px='18px' fontSize='14px' borderRadius='8px' colorScheme='red' onClick={onDelete} >Delete</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    )
}

export default ProductEditor;
